import { useRef } from 'react';
import type { UploadSlot } from '@lib/hooks';
import { Button } from './Button';
import { UploadStatusChip } from './UploadStatusChip';

/**
 * The control of a one-file field (inside a `Field`): the visible button carries the field's id, label and
 * help/error; then the file's name, its upload state and, after a passing failure, "Coba lagi". The file input itself
 * is hidden from the keyboard and screen readers.
 */
export function FileUploadField<K extends string>({ id, describedBy, invalid, accept, slot, onPick, onRetry, labels, photo }: {
  id: string;
  describedBy?: string;
  invalid?: boolean;
  accept: string;
  slot: UploadSlot<K> | undefined;
  onPick: (f: File) => void;
  onRetry: () => void;
  labels: { choose: string; replace: string; retry: string };
  photo?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="file-field">
      <input
        ref={input}
        type="file"
        accept={accept}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) onPick(f);
        }}
      />
      <Button id={id} size="sm" icon="upload" aria-describedby={describedBy} aria-invalid={invalid || undefined} onClick={() => input.current?.click()}>
        {slot ? labels.replace : labels.choose}
      </Button>
      {slot && <span className="file-field-name">{slot.name}</span>}
      {slot && (
        <span aria-live="polite">
          <UploadStatusChip state={slot} photo={photo} />
        </span>
      )}
      {slot?.status === 'failed' && slot.retryable && (
        <Button size="sm" variant="ghost" icon="refresh" onClick={onRetry}>
          {labels.retry}
        </Button>
      )}
    </div>
  );
}
